import { CdkPortal, DomPortalOutlet } from '@angular/cdk/portal';
import {
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  ComponentFactoryResolver,
  ElementRef,
  EmbeddedViewRef,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ITimelineMarker, IVideoMetadata } from '@epicuro-next/ataraxia/models';
import { isNonNullish } from '@epicuro-next/utilities';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import videojs, { VideoJsPlayerOptions } from 'video.js';

@Component({
  selector: 'atx-videojs-media-player',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './videojs-media-player.component.html',
  styleUrls: ['./videojs-media-player.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class VideojsMediaPlayerComponent implements OnChanges, OnDestroy {
  public videoDuration: number;

  private controlsEl: HTMLElement;
  private time: number;
  private embeddedViewRef: EmbeddedViewRef<unknown>;
  private destroy$ = new Subject<void>();

  @Input()
  public timelineMarkers: ITimelineMarker[];

  @Input()
  public src: string;

  @Input()
  public autoplay = true;

  @Input()
  public set playVideo(value: boolean) {
    if (value) {
      this.player?.play();
    } else {
      this.player?.pause();
    }
  }

  @Input()
  public enableFullScreen = false;

  @Input()
  public useNativeFullScreen = false;

  @Input()
  public defaultTimeStamp?: number;

  @Input()
  public defaultVolume?: number;

  @Output()
  public loadedMetadata: EventEmitter<IVideoMetadata> = new EventEmitter();

  @Output()
  public markerActive: EventEmitter<ITimelineMarker> = new EventEmitter();

  @Output()
  public timeUpdate: EventEmitter<number> = new EventEmitter();

  @Output()
  public volumeChange: EventEmitter<number> = new EventEmitter();

  @Output()
  public fullscreenClicked: EventEmitter<void> = new EventEmitter();

  @ViewChild('target', { static: true })
  public target: ElementRef;

  @ViewChild(CdkPortal, { static: true })
  private markersPortal: CdkPortal;

  private player: videojs.Player;

  constructor(
    private hostElement: ElementRef,
    private cfr: ComponentFactoryResolver,
    private ar: ApplicationRef,
    private injector: Injector,
    private renderer: Renderer2,
  ) {}

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.src && changes.src.previousValue !== changes.src.currentValue) {
      this.clearEmbeddedView();
      this.initializeVideoPlayer();
      this.player.src([{ src: this.src }]);
    }
  }

  public onMarkerClick(time: number) {
    this.player.currentTime(time);
  }

  public ngOnDestroy() {
    this.clearEmbeddedView();
    this.player?.dispose();
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get muted() {
    return !this.defaultVolume;
  }

  private clearEmbeddedView() {
    this.embeddedViewRef?.detach();
    this.embeddedViewRef?.destroy();
  }

  private initializeVideoPlayer() {
    if (this.player) return;
    this.player = videojs(
      this.target.nativeElement,
      {
        autoplay: this.autoplay,
        controls: true,
        controlBar: {
          fullscreenToggle: this.enableFullScreen,
        },
        defaultVolume: this.defaultVolume,
        fluid: true,
        userActions: {
          doubleClick: false,
        },
      } as VideoJsPlayerOptions,
      () => {
        this.player.play();
        this.player.on('loadedmetadata', () => this.onLoadedMetaData());
        this.player.on('timeupdate', () => this.onTimeUpdate());
        this.player.on('volumechange', () => {
          this.volumeChange.emit(this.player.volume());
        });

        if (!this.useNativeFullScreen && this.enableFullScreen) {
          this.swapFullscreenHandler();
        }
      },
    );
  }

  private onLoadedMetaData() {
    this.videoDuration = this.player.duration();
    this.controlsEl = this.hostElement.nativeElement.querySelector(
      '.vjs-progress-control',
    );

    this.loadedMetadata.emit({ duration: this.videoDuration });
    this.initMarkers();

    if (isNonNullish(this.defaultTimeStamp)) {
      this.player.currentTime(this.defaultTimeStamp);
    }

    if (isNonNullish(this.defaultVolume)) {
      this.player.volume(this.defaultVolume);
    }
  }

  private initMarkers() {
    this.embeddedViewRef = new DomPortalOutlet(
      this.controlsEl,
      this.cfr,
      this.ar,
      this.injector,
    ).attachTemplatePortal(this.markersPortal);
  }

  private onTimeUpdate() {
    // this timeupdate event handler is getting triggered couple of times per second, we need to check only of seconds changed
    const currentTime = Math.floor(this.player.currentTime());

    if (currentTime !== this.time) {
      this.time = currentTime;
      this.timeUpdate.emit(currentTime);

      const marker = this.timelineMarkers?.find((item) => {
        return item.start <= this.time && this.time <= item.end;
      });

      if (marker) {
        this.markerActive.emit(marker);
      }
    }
  }

  public trackByFn(index: number) {
    return index;
  }

  private swapFullscreenHandler() {
    const oldFullscreenElement = this.hostElement.nativeElement.querySelector(
      '.vjs-fullscreen-control',
    );
    const newFullScreenElement = oldFullscreenElement.cloneNode(true);
    this.renderer.removeChild(
      this.player.controlBar.el(),
      oldFullscreenElement,
    );
    this.renderer.appendChild(
      this.player.controlBar.el(),
      newFullScreenElement,
    );
    this.listenFullScreenClick(newFullScreenElement as HTMLButtonElement);
  }

  private listenFullScreenClick(newFullScreenElement: HTMLButtonElement) {
    fromEvent(newFullScreenElement, 'click')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.fullscreenClicked.emit());
  }
}
